import { bff, toClientError } from './bff'

const run = (p) => p.then((r) => r.data).catch((e) => Promise.reject(toClientError(e)))

// ---- Query ----
export const searchRag = ({ query, project, k = 8 }) =>
  run(bff.post('/api/rag/search', { query, project, k }))

// retrieval_mode (Graph RAG hybrid) and thread (follow-up condensation) MUST be
// forwarded — dropping retrieval_mode made the "+ explain" toggle a no-op and the
// request collide with the plain Graph RAG cache entry.
export const askRag = ({ question, project, strategy, response_language, use_cache, retrieval_mode, thread }) =>
  run(bff.post('/api/rag/ask', { question, project, strategy, response_language, use_cache, retrieval_mode, thread }))

export const listStrategies = () => run(bff.get('/api/rag/strategies'))

// ---- Q&A history (engine-owned; also the answer cache) ----
export const listHistory = (project) =>
  run(bff.get('/api/rag/history', { params: { project } }))

export const deleteHistory = ({ project, id }) =>
  run(bff.delete('/api/rag/history', { data: { project, id } }))

export const clearHistory = (project) =>
  run(bff.delete('/api/rag/history/all', { data: { project } }))

// ---- Projects ----
export const listProjects = () => run(bff.get('/api/rag/projects'))

export const createProject = ({ name, description, set_as_current, language }) =>
  run(bff.post('/api/rag/projects', { name, description, set_as_current, language }))

export const setProjectLanguage = ({ project, language }) =>
  run(bff.put('/api/rag/projects/language', { project, language }))

// Cross-engine teardown: deletes the project AND everything it owns across engines
// (vectors/docs, transcribe channels). Control-plane endpoint, not /api/rag.
export const deleteProject = (project) =>
  run(bff.delete(`/api/projects/${encodeURIComponent(project)}`, { timeout: 0 }))

// ---- Documents ----
export const listDocuments = (project, { limit, offset } = {}) =>
  run(bff.get('/api/rag/documents', { params: { project, limit, offset } }))

// Reassembled document text + per-chunk spans (for the viewer).
export const getDocument = ({ project, source }) =>
  run(bff.get('/api/rag/document', { params: { project, source } }))

export const deleteDocument = ({ file_path, project }) =>
  run(bff.delete('/api/rag/documents', { data: { file_path, project } }))

// Re-index a document from its (updated) source file (drops old chunks, re-embeds).
// No client timeout — re-embedding a large doc can take a while.
export const reindexDocument = ({ file_path, project }) =>
  run(bff.post('/api/rag/documents/reindex', { file_path, project }, { timeout: 0 }))

// Curation: exclude a document from all retrieval (or re-include), with a note.
export const curateDocument = ({ file_path, project, action, note }) =>
  run(bff.post('/api/rag/documents/curate', { file_path, project, action, note }, { timeout: 0 }))

// Scope ask: one question across multiple projects (merged retrieval, one answer).
export const askScope = ({ question, projects, k }) =>
  run(bff.post('/api/rag/ask-scope', { question, projects, k }, { timeout: 0 }))

// Staleness sweep: re-index changed files, add new ones (with path), report missing.
export const refreshStale = ({ project, path, kind, dry_run }) =>
  run(bff.post('/api/rag/documents/refresh-stale', { project, path, kind, dry_run }, { timeout: 0 }))

// Index a folder already on the server's machine (no upload).
// kind: 'code' walks the path as a codebase (code extensions, junk dirs skipped).
export const addPath = ({ project, path, kind }) =>
  run(bff.post('/api/rag/documents/add-path', { project, path, kind }, { timeout: 0 }))

// Dry-run codebase detection: per-language routing manifest (vector vs graph),
// nothing indexed — feeds the Scan → preview → Import flow.
export const scanCodePath = ({ path }) =>
  run(bff.post('/api/rag/documents/scan-path', { path }, { timeout: 0 }))

// Durable codebase import (saga): scan → vector batches (crash-resume) →
// bind repo → build code graph. Poll the returned saga_id via useSaga.
// graph:false skips the graph step; auto_sync:true registers the watcher;
// create_project:true creates the target project first (one-flow add).
export const importCodebase = ({ project, path, graph, auto_sync, create_project }) =>
  run(bff.post('/api/rag/codebases/import', { project, path, graph, auto_sync, create_project }))

// Add a repository that is NOT on the Veralis host yet: it clones the git URL
// into the workspace and then runs the normal index flow. `probe_only` checks
// the URL is readable (and reports where it would land) without cloning.
export const cloneCodebase = ({ url, project, folder, graph, auto_sync, create_project, probe_only }) =>
  run(bff.post('/api/rag/codebases/clone',
    { url, project, folder, graph, auto_sync, create_project, probe_only }, { timeout: 0 }))

// Git-aware repo status: HEAD/branch, recorded baseline, pending delta
// (added/modified/deleted). { is_git: false } → use the mtime refresh.
export const codebaseStatus = ({ project, path }) =>
  run(bff.get(`/api/rag/codebases/status?project=${encodeURIComponent(project)}&path=${encodeURIComponent(path)}`))

// Reconcile an indexed repo with its current git state (saga): upserts +
// real deletes, graph rebuild when touched, baseline advance.
export const syncCodebase = ({ project, path, full }) =>
  run(bff.post('/api/rag/codebases/sync', { project, path, full }))

// Per-repo auto-sync opt-in: the poller watches flagged repos and syncs on
// committed changes (hysteresis: quiet period, min batch, staleness ceiling, cap).
export const setCodebaseAutoSync = ({ project, path, enabled }) =>
  run(bff.patch('/api/rag/codebases/auto-sync', { project, path, enabled }))

// Browser upload — multipart. `project` field first so the BFF sees it before files.
export const uploadDocuments = ({ project, files, translateTo }) => {
  const fd = new FormData()
  fd.append('project', project)
  if (translateTo) fd.append('translate_to', translateTo)
  for (const f of files) fd.append('files', f, f.name)
  return run(
    bff.post('/api/rag/documents/upload', fd, {
      headers: { 'Content-Type': undefined }, // let the browser set the multipart boundary
    })
  )
}
